import { Component, TextComponent, Property } from '@wonderlandengine/api';
import { CRAFT_RECIPES } from './crafting-system.js';

// text-menu-hud.js — TextMenuHud
// Six in-world WLE text readouts wired to GameInterface.

const MAX_CONSOLE_LINES = 4;
const CONSOLE_WIDTH     = 44;   // characters — all lines padded to this length

/** Pad or truncate a line to exactly CONSOLE_WIDTH chars so center-aligned text forms a solid block. */
function padLine(str) {
    if (str.length > CONSOLE_WIDTH) return str.slice(0, CONSOLE_WIDTH);
    return str + ' '.repeat(CONSOLE_WIDTH - str.length);
}

export class TextMenuHud extends Component {
    static TypeName = 'text-menu-hud';

    static Properties = {
        phaseObj:      Property.object(), // "SCAVENGE"  "NIGHT"  "FORTIFY"
        waveObj:       Property.object(), // "3 / 10"
        biomeObj:      Property.object(), // "VOLCANIC"
        cargoTotalObj: Property.object(), // "142"
        consoleObj:    Property.object(), // rolling log
        toolObj:       Property.object(), // current tool + controls (2 lines)
    };

    // Two-line static labels for non-crafting tools
    static _TOOL_TEXT = {
        mining: 'MINING TOOL\n[R.TRIG] MINE   [L.TRIG] BUILD   [A] CYCLE MAT',
        pistol: 'PHASE PISTOL\n[R.TRIG] FIRE   [L.TRIG] RELOAD',
    };

    start() {
        this._cache = {};
        this._tc = {
            phase:      this._get(this.phaseObj),
            wave:       this._get(this.waveObj),
            biome:      this._get(this.biomeObj),
            cargoTotal: this._get(this.cargoTotalObj),
            console:    this._get(this.consoleObj),
            tool:       this._get(this.toolObj),
        };

        this._set('phase',      'STANDBY');
        this._set('wave',       '');
        this._set('biome',      '---');
        this._set('cargoTotal', '0');
        this._set('console',    padLine('[SYS] ONLINE'));
        this._set('tool',       TextMenuHud._TOOL_TEXT['mining']);

        this._log          = [padLine('[SYS] ONLINE')];
        this._activeTool   = 'mining';
        this._craftingOpen = false;
        this._recipeIdx    = 0;      // which recipe is highlighted in crafting
        this._isCrafting   = false;  // craft in progress
        this._craftPct     = 0;      // 0–1 craft progress

        window.TextMenuHud = this;

        // Hidden by default — activated in update() when a VR session is detected.
        this._inVR = false;
        this._setTextsActive(false);

        this._wire();
    }

    update() {
        const inVR = !!(this.engine.xr?.session);
        if (inVR !== this._inVR) {
            this._inVR = inVR;
            this._setTextsActive(inVR);
        }
    }

    _wire() {
        const gi   = window.GameInterface = window.GameInterface || {};
        const prev = {};
        const wrap = (name, fn) => { prev[name] = gi[name]; gi[name] = (...a) => { prev[name]?.(...a); fn(...a); }; };

        const PHASE_NAMES = {
            deploy: 'DEPLOYING', scavenge: 'SCAVENGE', fortify: 'FORTIFY',
            survive: 'NIGHT', extract: 'EXTRACT', ended: 'ENDED', lobby: 'STANDBY',
        };

        wrap('setPhase', (phase, secondsLeft) => {
            const label = PHASE_NAMES[phase] ?? phase.toUpperCase();
            this._set('phase', secondsLeft > 0 ? `${label}  ${secondsLeft}s` : label);
        });

        wrap('setWave', (done, target) => {
            if (done == null || target == null) { this._set('wave', ''); return; }
            this._set('wave', `${done} / ${target}`);
        });

        wrap('updateResources', (alloy, biomass, titanium, plasma, crystal) => {
            this._set('cargoTotal', String(alloy + biomass + titanium + plasma + crystal));
            // Refresh craft affordability indicator whenever resources change
            if (this._craftingOpen && !this._isCrafting) this._updateCraftText();
        });

        wrap('pushConsoleLog', (msg) => {
            const prefix = msg.includes('MINED') || msg.includes('CLEAR') || msg.includes('SHARDS') ? '[+] '
                         : msg.includes('INSUFFICIENT') || msg.includes('HORDE') || msg.includes('WAVE') ? '[!] '
                         : '[>] ';
            this._log.push(padLine(prefix + msg));
            if (this._log.length > MAX_CONSOLE_LINES) this._log.shift();
            this._set('console', this._log.join('\n'));
        });

        // ── Tool indicator ────────────────────────────────────────────────────
        // VRControls._switchTool → setSelectedWeapon; weapon wheel → selectWeapon.
        const showTool = (id) => {
            this._activeTool = id;
            if (!this._craftingOpen) {
                this._set('tool', TextMenuHud._TOOL_TEXT[id] ?? id.toUpperCase());
            }
        };
        wrap('setSelectedWeapon', showTool);
        wrap('selectWeapon',      showTool);

        // ── Crafting panel open / close ───────────────────────────────────────
        // VRControls calls toggleCraftingPanel(bool); PlayerInput does too (desktop C key).
        wrap('toggleCraftingPanel', (open) => {
            this._craftingOpen = !!open;
            if (open) {
                this._recipeIdx  = 0;
                this._isCrafting = false;
                this._craftPct   = 0;
                this._updateCraftText();
            } else {
                this._isCrafting = false;
                this._set('tool', TextMenuHud._TOOL_TEXT[this._activeTool] ?? this._activeTool.toUpperCase());
            }
        });

        // ── Craft progress ────────────────────────────────────────────────────
        // CraftingSystem calls updateCraftProgress(recipeId, 0–1) every frame
        // while crafting; passes null recipeId when done/cancelled.
        wrap('updateCraftProgress', (recipeId, pct) => {
            if (recipeId) {
                this._isCrafting = true;
                this._craftPct   = pct ?? 0;
                // Keep highlighted index in sync with what's actually being crafted
                const idx = CRAFT_RECIPES.findIndex(r => r.id === recipeId);
                if (idx >= 0) this._recipeIdx = idx;
            } else {
                this._isCrafting = false;
                this._craftPct   = 0;
            }
            if (this._craftingOpen) this._updateCraftText();
        });

        // Friendly display names for all biome IDs
        gi._biomeNames = {
            grasslands:       'THE VERDANT',
            tundra:           'FROST FLATS',
            alien_jungle:     'GREEN MIST',
            volcanic:         'EMBER FIELDS',
            canyon:           'RED COUNTRY',
            fallen_city:      'CHALK SHORES',
            cyber_grid:       'PALE STEPPE',
            flesh_hive:       'AMBER HILLS',
            void_archipelago: 'LOST ISLES',
            neon_abyss:       'PAINTED DUST',
            shattered_realm:  'STONE CROWN',
            fungal_cathedral: 'STILL WATER',
            ancient_ruins:    'GOLDEN SHORE',
        };

        // Biome updated by radar tick — hook into the existing DOM update path
        const origRadar = gi.updateRadar;
        gi.updateRadar = () => {
            origRadar?.();
            try {
                const vt = window.VoxelTreadmill;
                const buf = new Float32Array(3);
                vt.object.getPositionWorld(buf);
                const biome = vt._sampler?.dominantBiomeAt?.(-buf[0], -buf[2]);
                if (biome) {
                    const name = gi._biomeNames?.[biome.id] ?? biome.id.toUpperCase().replace(/_/g, ' ');
                    this._set('biome', name);
                }
            } catch (_) {}
        };
    }

    // ── Crafting public API (called by VRControls) ────────────────────────────

    /** Advance to the next recipe; wraps around. Updates text immediately. */
    cycleCraftRecipe() {
        if (this._isCrafting) return; // can't cycle while craft is running
        this._recipeIdx = (this._recipeIdx + 1) % CRAFT_RECIPES.length;
        this._updateCraftText();
    }

    /** Return the id of the currently highlighted recipe, or null. */
    currentRecipeId() {
        return CRAFT_RECIPES[this._recipeIdx]?.id ?? null;
    }

    // ── Internal ──────────────────────────────────────────────────────────────

    /** Rebuild and push the crafting text for the current recipe/state. */
    _updateCraftText() {
        const recipe = CRAFT_RECIPES[this._recipeIdx];
        if (!recipe) return;

        const total = CRAFT_RECIPES.length;
        const num   = this._recipeIdx + 1;

        if (this._isCrafting) {
            // Show ASCII progress bar + percentage
            const bar   = this._progressBar(this._craftPct);
            const pctStr = Math.round(this._craftPct * 100) + '%';
            this._set('tool',
                `CRAFTING: ${recipe.name}\n${bar} ${pctStr}   [L.GRIP] CANCEL`
            );
            return;
        }

        // Build ingredient string: "PLASMA x3  ALLOY x2"
        const needs = Object.entries(recipe.inputs)
            .map(([mat, amt]) => `${mat.toUpperCase()} x${amt}`)
            .join('  ');

        // Check affordability live from CraftingSystem
        const canCraft = window.CraftingSystem?.canCraft?.(recipe.id) ?? false;
        const status   = canCraft ? '' : '  [!]';

        this._set('tool',
            `[${num}/${total}] ${recipe.name}${status}\n${needs}   [R.TRIG] CRAFT   [L.TRIG] NEXT`
        );
    }

    /** Render a fixed-width 12-char ASCII progress bar for 0–1 fraction. */
    _progressBar(pct) {
        const W      = 12;
        const filled = Math.round(Math.min(1, pct) * W);
        return '[' + '#'.repeat(filled) + '-'.repeat(W - filled) + ']';
    }

    _setTextsActive(val) {
        for (const [key, tc] of Object.entries(this._tc)) {
            if (!tc) continue;
            tc.active = val;
            tc.text   = val ? (this._cache[key] ?? '') : '';
        }
    }

    _get(obj) { return obj?.getComponent(TextComponent) ?? null; }
    _set(key, val) {
        this._cache[key] = val;
        const tc = this._tc?.[key];
        if (tc && tc.active) tc.text = val;
    }
}
